import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Broom, CookingPot, Baby, Car, House, Heart, Plant, Coffee, Newspaper,
  FirstAid, GraduationCap, UserCircle, Plus, X, SignIn, SignOut, Trash, MagnifyingGlass, Clock,
  Camera, IdentificationCard, ShieldCheck, Buildings, SlidersHorizontal,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { HELP_TYPES, helpTypeMeta } from "../lib/visitors";

const ICONS = { Broom, CookingPot, Baby, Car, House, Heart, Plant, Coffee, Newspaper, FirstAid, GraduationCap, UserCircle };
const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const ID_PROOFS = [
  { code: "AADHAAR", label: "Aadhaar" }, { code: "VOTER", label: "Voter ID" }, { code: "PAN", label: "PAN" },
  { code: "DL", label: "Driving licence" }, { code: "PASSPORT", label: "Passport" }, { code: "OTHER", label: "Other" },
];

// Upload an image File to B2 via the gate photo endpoint; returns its storage key.
async function uploadHelpImage(file) {
  const fd = new FormData();
  fd.append("file", file, file.name || "photo.jpg");
  const { data } = await api.post("/api/visitors/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { photoKey, photoUrl }
}

// "C-1808" or "C 1808" -> "C-1808" (matches flat id format used everywhere).
function normFlatId(s) {
  return String(s || "").trim().toUpperCase().replace(/\s+/g, "-").replace(/-+/g, "-");
}
const flatsLabel = (h) => (h.flatIds && h.flatIds.length ? h.flatIds : [h.flatLabel].filter(Boolean)).join(", ");

function todayLabel(t) {
  if (!t) return null;
  if (t.inAt && t.outAt) return `In ${fmt(t.inAt)} · Out ${fmt(t.outAt)}`;
  if (t.inAt) return `Inside · in ${fmt(t.inAt)}`;
  if (t.outAt) return `Left ${fmt(t.outAt)}`;
  return null;
}

/* ───────── Guard: mark daily-help attendance ───────── */
export function GateHelp() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [manageFor, setManageFor] = useState(null);
  const load = useCallback((query = "") => {
    api.get(`/api/dailyhelp${query ? `?q=${encodeURIComponent(query)}` : ""}`).then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(() => load(q), 10000); return () => clearInterval(id); }, [load, q]);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q, load]);

  return (
    <div className="space-y-3" data-testid="gate-help">
      <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.03] px-3">
        <MagnifyingGlass size={16} className="text-white/40" />
        <input className="flex-1 bg-transparent py-3 text-white outline-none" placeholder="Search helper by name, flat or code" value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search" />
      </div>
      {items.length === 0 ? (
        <div className="card p-8 text-center text-sm text-white/45">No daily help registered{q ? " for that search" : " yet"}.</div>
      ) : items.map((h) => <GateHelpCard key={h.id} h={h} onChange={() => load(q)} onManage={() => setManageFor(h)} />)}
      <AnimatePresence>
        {manageFor && <StaffHelpManageSheet helper={manageFor} onClose={() => setManageFor(null)} onSaved={() => { setManageFor(null); load(q); }} />}
      </AnimatePresence>
    </div>
  );
}

function GateHelpCard({ h, onChange, onManage }) {
  const meta = helpTypeMeta(h.type); const Icon = ICONS[meta.icon] || UserCircle;
  const [busy, setBusy] = useState(false);
  const inside = h.today?.inAt && !h.today?.outAt;
  const verified = !!h.kycVerified;
  const shared = (h.flatIds || []).length > 1;
  async function mark(direction) {
    setBusy(true);
    try { await api.post(`/api/dailyhelp/${h.id}/mark`, { direction }); onChange(); } catch {} finally { setBusy(false); }
  }
  return (
    <div className="card p-3.5" data-testid={`help-${h.id}`}>
      <div className="flex items-center gap-3">
        <button onClick={onManage} className="relative h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-white/[0.05]">
          {h.photoUrl ? <img src={h.photoUrl} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-neon-cyan"><Icon size={22} weight="duotone" /></span>}
        </button>
        <button onClick={onManage} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <p className="truncate font-display text-[15px] font-semibold text-white">{h.name}</p>
            {inside && <span className="rounded-md border border-neon-lime/30 bg-neon-lime/10 px-1.5 py-0.5 text-[10px] font-bold text-neon-lime">Inside</span>}
          </div>
          <p className="truncate text-[12px] text-white/55">{meta.label} · <span className="font-mono text-white/80">{flatsLabel(h)}</span> · ID {h.code}{todayLabel(h.today) ? ` · ${todayLabel(h.today)}` : ""}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${verified ? "border-neon-lime/30 bg-neon-lime/10 text-neon-lime" : "border-neon-amber/30 bg-neon-amber/10 text-neon-amber"}`}>
              <ShieldCheck size={11} weight="bold" /> {verified ? "KYC OK" : "Pending KYC"}
            </span>
            {h.policeVerified && <span className="rounded-md border border-neon-cyan/30 px-1.5 py-0.5 text-[10px] font-bold text-neon-cyan">POLICE</span>}
            {shared && <span className="inline-flex items-center gap-1 rounded-md border border-white/15 px-1.5 py-0.5 text-[10px] font-bold text-white/60"><Buildings size={11} weight="bold" /> {h.flatIds.length} FLATS</span>}
          </div>
        </button>
        <div className="flex shrink-0 flex-col gap-1.5">
          <button disabled={busy} onClick={() => mark("IN")} data-testid={`help-in-${h.id}`}
            className="inline-flex items-center gap-1 rounded-xl bg-neon-lime/15 px-3 py-1.5 text-[12px] font-bold text-neon-lime disabled:opacity-50"><SignIn size={14} weight="bold" /> In</button>
          <button disabled={busy} onClick={() => mark("OUT")} data-testid={`help-out-${h.id}`}
            className="inline-flex items-center gap-1 rounded-xl bg-white/5 px-3 py-1.5 text-[12px] font-bold text-white/80 disabled:opacity-50"><SignOut size={14} weight="bold" /> Out</button>
        </div>
      </div>
      <button onClick={onManage} data-testid={`help-manage-${h.id}`}
        className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] py-2 text-[11px] font-bold uppercase tracking-wider text-neon-cyan hover:bg-white/[0.06]">
        <SlidersHorizontal size={13} weight="bold" /> Details, photo &amp; KYC
      </button>
    </div>
  );
}

/* ───────── Staff (Manager + Security): manage helper detail, photo, KYC docs, flats ───────── */
function StaffHelpManageSheet({ helper, onClose, onSaved }) {
  const meta = helpTypeMeta(helper.type); const Icon = ICONS[meta.icon] || UserCircle;
  const [photoUrl, setPhotoUrl] = useState(helper.photoUrl || null);
  const [photoKey, setPhotoKey] = useState(null);
  const [idType, setIdType] = useState(helper.idProofType || "AADHAAR");
  const [idNum, setIdNum] = useState(helper.idProofNumber || "");
  const [idProofUrl, setIdProofUrl] = useState(helper.idProofPhotoUrl || null);
  const [idProofKey, setIdProofKey] = useState(null);
  const [police, setPolice] = useState(!!helper.policeVerified);
  const [policeDocUrl, setPoliceDocUrl] = useState(helper.policeDocPhotoUrl || null);
  const [policeDocKey, setPoliceDocKey] = useState(null);
  const [flatsText, setFlatsText] = useState((helper.flatIds || [helper.flatLabel].filter(Boolean)).join(", "));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // 'photo' | 'id' | 'police'
  const [err, setErr] = useState(null);

  async function pick(kind, ev) {
    const file = ev.target.files?.[0]; ev.target.value = "";
    if (!file) return;
    setUploading(kind); setErr(null);
    try {
      const { photoKey: key, photoUrl: url } = await uploadHelpImage(file);
      if (kind === "photo") { setPhotoKey(key); setPhotoUrl(url); }
      else if (kind === "id") { setIdProofKey(key); setIdProofUrl(url); }
      else { setPoliceDocKey(key); setPoliceDocUrl(url); }
    } catch { setErr("Upload failed. Please try again."); } finally { setUploading(null); }
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      // 1) Flats + (photo lives on the helper doc) via edit endpoint.
      const flatIds = flatsText.split(",").map(normFlatId).filter(Boolean);
      const editBody = {};
      if (flatIds.length) editBody.flatIds = flatIds;
      if (Object.keys(editBody).length) await api.patch(`/api/dailyhelp/${helper.id}`, editBody);
      // 2) KYC: photo + ID proof + police doc + flags.
      const kyc = { idProofType: idType, idProofNumber: idNum.trim() || null, policeVerified: police };
      if (photoKey) kyc.photoKey = photoKey;
      if (idProofKey) kyc.idProofPhotoKey = idProofKey;
      if (policeDocKey) kyc.policeDocPhotoKey = policeDocKey;
      await api.post(`/api/dailyhelp/${helper.id}/kyc`, kyc);
      onSaved();
    } catch { setErr("Could not save. Please try again."); setBusy(false); }
  }

  const UploadTile = ({ kind, label, url, icon: TileIcon }) => (
    <label className="flex cursor-pointer flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-3 hover:border-neon-cyan/40">
      <div className="relative h-20 w-full overflow-hidden rounded-xl bg-white/[0.04]">
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-white/35"><TileIcon size={26} weight="duotone" /></span>}
        {uploading === kind && <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-[11px] font-bold text-white">Uploading…</span>}
      </div>
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-neon-cyan"><Camera size={13} weight="bold" /> {label}</span>
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(kind, e)} />
    </label>
  );

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-lg overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:max-h-[90vh] sm:rounded-[2rem]" data-testid="staff-help-manage">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 overflow-hidden rounded-2xl bg-white/[0.05]">
              {photoUrl ? <img src={photoUrl} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-neon-cyan"><Icon size={22} weight="duotone" /></span>}
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-white">{helper.name}</h3>
              <p className="text-[12px] text-white/55">{meta.label} · ID {helper.code}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>

        <div className="mt-5 space-y-5">
          {/* photo + documents */}
          <div>
            <p className="label mb-2 flex items-center gap-1.5"><Camera size={13} /> Photo &amp; documents</p>
            <div className="grid grid-cols-3 gap-2">
              <UploadTile kind="photo" label="Photo" url={photoUrl} icon={UserCircle} />
              <UploadTile kind="id" label="ID proof" url={idProofUrl} icon={IdentificationCard} />
              <UploadTile kind="police" label="Police doc" url={policeDocUrl} icon={ShieldCheck} />
            </div>
          </div>

          {/* ID proof details */}
          <div>
            <p className="label mb-2">ID proof type</p>
            <div className="flex flex-wrap gap-2">
              {ID_PROOFS.map((p) => (
                <button key={p.code} onClick={() => setIdType(p.code)}
                  className={`rounded-xl border px-3 py-1.5 text-[12px] font-semibold ${idType === p.code ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/65"}`}>{p.label}</button>
              ))}
            </div>
            <label className="label mb-1 mt-3 block">ID number</label>
            <input className="field" value={idNum} onChange={(e) => setIdNum(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 20))} placeholder="e.g. last 8–12 digits/chars" />
          </div>

          {/* police verification */}
          <button onClick={() => setPolice((v) => !v)} className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left">
            <span className={`flex h-6 w-6 items-center justify-center rounded-md border ${police ? "border-neon-lime bg-neon-lime text-black" : "border-white/25"}`}>{police && <ShieldCheck size={14} weight="bold" />}</span>
            <span className="flex-1">
              <span className="block text-[13px] font-bold text-white">Police verification done</span>
              <span className="block text-[11px] text-white/55">Confirms a background check is on file</span>
            </span>
          </button>

          {/* multi-flat */}
          <div>
            <label className="label mb-1 flex items-center gap-1.5"><Buildings size={13} /> Hired flats (comma separated)</label>
            <input className="field font-mono" value={flatsText} onChange={(e) => setFlatsText(e.target.value)} placeholder="e.g. C-1808, C-1907, C-404" />
            <p className="mt-1 text-[11px] text-white/45">Check-in / check-out alerts go to the residents of every flat listed here.</p>
          </div>

          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={busy || !!uploading} onClick={save} data-testid="staff-help-save" className="btn-primary w-full disabled:opacity-50">{busy ? "Saving…" : "Save details"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ───────── Resident: manage helpers + see attendance ───────── */
export function ResidentHelp() {
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const load = useCallback(() => { api.get("/api/dailyhelp").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  return (
    <div className="space-y-3" data-testid="resident-help">
      <button onClick={() => setAddOpen(true)} data-testid="add-help-btn"
        className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/[0.02] py-3 text-sm font-bold text-white/80 hover:border-neon-cyan/40">
        <Plus size={16} weight="bold" /> Add daily help
      </button>
      {items.length === 0 ? (
        <div className="card p-6 text-center text-sm text-white/45">Register your maid, cook, driver and more, then the gate tracks their daily in/out.</div>
      ) : items.map((h) => <ResidentHelpCard key={h.id} h={h} onChange={load} onHistory={() => setHistory(h)} />)}
      <AnimatePresence>
        {addOpen && <AddHelpSheet onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
        {history && <HistorySheet helper={history} onClose={() => setHistory(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ResidentHelpCard({ h, onChange, onHistory }) {
  const meta = helpTypeMeta(h.type); const Icon = ICONS[meta.icon] || UserCircle;
  const inside = h.today?.inAt && !h.today?.outAt;
  async function remove() { await api.post(`/api/dailyhelp/${h.id}/remove`); onChange(); }
  return (
    <div className="card flex items-center gap-3 p-3.5">
      <button onClick={onHistory} className="relative h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-white/[0.05]">
        {h.photoUrl ? <img src={h.photoUrl} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-neon-cyan"><Icon size={22} weight="duotone" /></span>}
      </button>
      <button onClick={onHistory} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <p className="truncate font-display text-[15px] font-semibold text-white">{h.name}</p>
          {inside && <span className="rounded-md border border-neon-lime/30 bg-neon-lime/10 px-1.5 py-0.5 text-[10px] font-bold text-neon-lime">Inside</span>}
        </div>
        <p className="truncate text-[12px] text-white/55">{meta.label} · ID {h.code}{todayLabel(h.today) ? ` · ${todayLabel(h.today)}` : " · not arrived"}</p>
      </button>
      <button onClick={remove} className="rounded-full bg-white/5 p-2 text-white/55 hover:text-neon-pink" title="Remove"><Trash size={15} weight="bold" /></button>
    </div>
  );
}

function AddHelpSheet({ onClose, onAdded }) {
  const [type, setType] = useState("MAID");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const valid = name.trim().length >= 2;
  async function submit() {
    if (!valid) { setErr("Enter a name."); return; }
    setBusy(true); setErr(null);
    try { await api.post("/api/dailyhelp", { type, name: name.trim(), phone: phone || null }); onAdded(); }
    catch { setErr("Could not add. Try again."); setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-lg overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="add-help-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Add daily help</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {HELP_TYPES.map((t) => {
              const I = ICONS[t.icon] || UserCircle; const on = type === t.code;
              return (
                <button key={t.code} onClick={() => setType(t.code)}
                  className={`flex flex-col items-center gap-1 rounded-2xl border px-2 py-2.5 text-[11.5px] font-semibold transition ${on ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/65"}`}>
                  <I size={20} weight={on ? "fill" : "duotone"} /> {t.label}
                </button>
              );
            })}
          </div>
          <div><label className="label">Helper name</label>
            <input className="field" data-testid="help-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunita" /></div>
          <div><label className="label">Phone (optional)</label>
            <input className="field" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit" /></div>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} data-testid="help-submit" className="btn-primary w-full disabled:opacity-50">{busy ? "Adding…" : "Add helper"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function HistorySheet({ helper, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/api/dailyhelp/${helper.id}/attendance?days=14`).then(({ data }) => setRows(data.items || [])).catch(() => setRows([])); }, [helper.id]);
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-white">{helper.name}</h3>
            <p className="text-[12px] text-white/55">{helpTypeMeta(helper.type).label} · Helper ID {helper.code}</p>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <p className="mt-4 mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">Last 14 days</p>
        {rows === null ? <p className="text-sm text-white/45">Loading…</p>
          : rows.length === 0 ? <p className="text-sm text-white/45">No attendance recorded yet.</p>
          : (
            <div className="divide-y divide-white/5">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2.5">
                  <span className="text-[13px] text-white/80">{new Date(r.day).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })}</span>
                  <span className="flex items-center gap-1.5 text-[12px] text-white/55"><Clock size={13} />{r.inAt ? fmt(r.inAt) : "—"} → {r.outAt ? fmt(r.outAt) : "—"}</span>
                </div>
              ))}
            </div>
          )}
      </motion.div>
    </motion.div>
  );
}
